import React, { useState, useEffect, useCallback } from 'react';
import { Kiosk } from '../types';
import { kioskService } from '../services/api';

const KioskManagement: React.FC = () => {
  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKioskName, setNewKioskName] = useState('');
  const [newKioskLocation, setNewKioskLocation] = useState('');
  const [newPaymentProvider, setNewPaymentProvider] = useState<'payconiq' | 'viva'>('payconiq');
  const [newPaymentMode, setNewPaymentMode] = useState<'qr' | 'pos'>('qr'); // qr for payconiq, pos (nfc) for viva
  const [newTestingMode, setNewTestingMode] = useState(false);
  const [newAskEmail, setNewAskEmail] = useState(false);
  const [createdKiosk, setCreatedKiosk] = useState<Kiosk | null>(null);
  const [editingKiosk, setEditingKiosk] = useState<Kiosk | null>(null);
  const [editPaymentProvider, setEditPaymentProvider] = useState<'payconiq' | 'viva'>('payconiq');
  const [editPaymentMode, setEditPaymentMode] = useState<'qr' | 'pos'>('qr'); // qr for payconiq, pos (nfc) for viva
  const [editTestingMode, setEditTestingMode] = useState(false);
  const [editAskEmail, setEditAskEmail] = useState(false);
  const itemsPerPage = 20;

  const fetchKiosks = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await kioskService.getAll(currentPage, itemsPerPage, statusFilter, activeSearchQuery);
      setKiosks(response.data);
      setTotalPages(Math.ceil(response.total / itemsPerPage));
    } catch (err: any) {
      console.error('Failed to fetch kiosks:', err);
      setError(err.response?.data?.message || 'Failed to load kiosks');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, statusFilter, activeSearchQuery]);

  useEffect(() => {
    fetchKiosks();
  }, [fetchKiosks]);

  const handleSearch = () => {
    setActiveSearchQuery(searchQuery);
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleCreateKiosk = async () => {
    if (!newKioskName.trim()) {
      setError('Kiosk name is required');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const kiosk = await kioskService.create({
        name: newKioskName,
        location: newKioskLocation || undefined,
        payment_provider: newPaymentProvider,
        payment_mode: newPaymentMode,
        testing_mode: newTestingMode,
        ask_email: newAskEmail,
      });
      setCreatedKiosk(kiosk);
      setSuccess(`Kiosk "${kiosk.name}" created successfully!`);
      setNewKioskName('');
      setNewKioskLocation('');
      setNewPaymentProvider('payconiq');
      setNewPaymentMode('qr');
      setNewTestingMode(false);
      setNewAskEmail(false);
      fetchKiosks();
    } catch (err: any) {
      console.error('Failed to create kiosk:', err);
      setError(err.response?.data?.message || 'Failed to create kiosk');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to get the correct payment mode for a provider
  const getPaymentModeForProvider = (provider: 'payconiq' | 'viva'): 'qr' | 'pos' => {
    return provider === 'payconiq' ? 'qr' : 'pos';
  };

  // Handler for changing payment provider in create modal
  const handleNewPaymentProviderChange = (provider: 'payconiq' | 'viva') => {
    setNewPaymentProvider(provider);
    setNewPaymentMode(getPaymentModeForProvider(provider));
  };

  // Handler for changing payment provider in edit modal
  const handleEditPaymentProviderChange = (provider: 'payconiq' | 'viva') => {
    setEditPaymentProvider(provider);
    setEditPaymentMode(getPaymentModeForProvider(provider));
  };

  const handleEditKiosk = (kiosk: Kiosk) => {
    setEditingKiosk(kiosk);
    const provider = kiosk.payment_config?.payment_provider || 'payconiq';
    setEditPaymentProvider(provider);
    setEditPaymentMode(getPaymentModeForProvider(provider));
    setEditTestingMode(kiosk.payment_config?.testing_mode || false);
    setEditAskEmail(kiosk.payment_config?.ask_email || false);
  };

  const handleSaveEdit = async () => {
    if (!editingKiosk) return;

    try {
      setIsLoading(true);
      setError(null);
      await kioskService.update(editingKiosk.id, {
        payment_provider: editPaymentProvider,
        payment_mode: editPaymentMode,
        testing_mode: editTestingMode,
        ask_email: editAskEmail,
      });
      setSuccess(`Kiosk "${editingKiosk.name}" updated successfully!`);
      setEditingKiosk(null);
      fetchKiosks();
    } catch (err: any) {
      console.error('Failed to update kiosk:', err);
      setError(err.response?.data?.message || 'Failed to update kiosk');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (kiosk: Kiosk, newStatus: 'active' | 'inactive') => {
    try {
      setIsLoading(true);
      setError(null);
      const updatedKiosk = await kioskService.update(kiosk.id, { status: newStatus });

      // If reactivating (status changed to 'active'), show the new activation code
      if (newStatus === 'active' && (kiosk.status === 'inactive' || kiosk.status === 'active')) {
        setCreatedKiosk(updatedKiosk);
        setSuccess(`Kiosk "${kiosk.name}" reset successfully! New activation code generated.`);
      } else {
        setSuccess(`Kiosk "${kiosk.name}" ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      }

      fetchKiosks();
    } catch (err: any) {
      console.error('Failed to update kiosk:', err);
      setError(err.response?.data?.message || 'Failed to update kiosk');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteKiosk = async (kiosk: Kiosk) => {
    if (!window.confirm(`Are you sure you want to delete kiosk "${kiosk.name}"?`)) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await kioskService.delete(kiosk.id);
      setSuccess(`Kiosk "${kiosk.name}" deleted successfully`);
      fetchKiosks();
    } catch (err: any) {
      console.error('Failed to delete kiosk:', err);
      setError(err.response?.data?.message || 'Failed to delete kiosk');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const closeActivationModal = () => {
    setCreatedKiosk(null);
    setShowCreateModal(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Kiosk Management</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          + Create Kiosk
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{success}</span>
          <button
            onClick={() => setSuccess(null)}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button
            onClick={() => setError(null)}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex gap-4">
        <div className="flex-1">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              placeholder="Search by name or location..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Search
            </button>
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Kiosks Table */}
      {isLoading && kiosks.length === 0 ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : kiosks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No kiosks found
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activation Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Used</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {kiosks.map((kiosk) => (
                  <tr key={kiosk.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{kiosk.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{kiosk.location || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(kiosk.status)}`}>
                        {kiosk.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {kiosk.payment_config ? (
                        <span className="text-xs">
                          <span className="font-medium capitalize">{kiosk.payment_config.payment_provider}</span>
                          <span className="text-gray-400"> / </span>
                          <span className={`${kiosk.payment_config.payment_mode === 'pos' ? 'text-purple-600 font-medium' : 'text-orange-600 font-medium'}`}>
                            {kiosk.payment_config.payment_mode === 'pos' ? 'NFC' : 'QR'}
                          </span>
                          {kiosk.payment_config.testing_mode && (
                            <span className="ml-2 px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                              TEST
                            </span>
                          )}
                          {kiosk.payment_config.ask_email && (
                            <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                              EMAIL
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">Default</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {kiosk.status === 'pending' ? (
                        <span className="font-bold text-blue-600">{kiosk.activation_code}</span>
                      ) : (
                        <span className="text-gray-400">Used</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(kiosk.last_used_at)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEditKiosk(kiosk)}
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                      >
                        Edit
                      </button>
                      {kiosk.status === 'active' ? (
                        <>
                          <button
                            onClick={() => handleUpdateStatus(kiosk, 'inactive')}
                            className="text-orange-600 hover:text-orange-900 mr-3"
                          >
                            Deactivate
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(kiosk, 'active')}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            title="Reset kiosk with new activation code"
                          >
                            Reset
                          </button>
                        </>
                      ) : kiosk.status === 'inactive' ? (
                        <button
                          onClick={() => handleUpdateStatus(kiosk, 'active')}
                          className="text-green-600 hover:text-green-900 mr-3"
                          title="Generate new activation code"
                        >
                          Reactivate
                        </button>
                      ) : null}
                      <button
                        onClick={() => handleDeleteKiosk(kiosk)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Kiosk Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Create New Kiosk</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={newKioskName}
                  onChange={(e) => setNewKioskName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Main Entrance Kiosk"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={newKioskLocation}
                  onChange={(e) => setNewKioskLocation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Building A, Floor 1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Provider
                  </label>
                  <select
                    value={newPaymentProvider}
                    onChange={(e) => handleNewPaymentProviderChange(e.target.value as 'payconiq' | 'viva')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="payconiq">Payconiq</option>
                    <option value="viva">Viva</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Mode
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
                    {newPaymentProvider === 'payconiq' ? (
                      <span>QR Code <span className="text-gray-500 text-xs">(scan with phone)</span></span>
                    ) : (
                      <span>NFC <span className="text-gray-500 text-xs">(tap card on device)</span></span>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                {newPaymentProvider === 'payconiq'
                  ? 'Payconiq: User scans QR code with phone to pay, receives ticket on phone'
                  : 'Viva NFC: User taps card on Android kiosk, kiosk displays ticket QR for user to scan'}
              </p>
              <div className="flex items-center gap-3 pt-2">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newTestingMode}
                    onChange={(e) => setNewTestingMode(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">Testing Mode</span>
                </label>
                <span className="text-xs text-gray-500">(Auto-completes payments)</span>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newAskEmail}
                    onChange={(e) => setNewAskEmail(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">Ask for Email</span>
                </label>
                <span className="text-xs text-gray-500">(Collect email before NFC payment)</span>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={handleCreateKiosk}
                disabled={isLoading}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Create
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Kiosk Modal */}
      {editingKiosk && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Edit Kiosk: {editingKiosk.name}</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Provider
                  </label>
                  <select
                    value={editPaymentProvider}
                    onChange={(e) => handleEditPaymentProviderChange(e.target.value as 'payconiq' | 'viva')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="payconiq">Payconiq</option>
                    <option value="viva">Viva</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Mode
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
                    {editPaymentProvider === 'payconiq' ? (
                      <span>QR Code <span className="text-gray-500 text-xs">(scan with phone)</span></span>
                    ) : (
                      <span>NFC <span className="text-gray-500 text-xs">(tap card on device)</span></span>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                {editPaymentProvider === 'payconiq'
                  ? 'Payconiq: User scans QR code with phone to pay, receives ticket on phone'
                  : 'Viva NFC: User taps card on Android kiosk, kiosk displays ticket QR for user to scan'}
              </p>
              <div className="flex items-center gap-3 pt-2">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editTestingMode}
                    onChange={(e) => setEditTestingMode(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">Testing Mode</span>
                </label>
                <span className="text-xs text-gray-500">(Auto-completes payments)</span>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editAskEmail}
                    onChange={(e) => setEditAskEmail(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">Ask for Email</span>
                </label>
                <span className="text-xs text-gray-500">(Collect email before NFC payment)</span>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={handleSaveEdit}
                disabled={isLoading}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Save
              </button>
              <button
                onClick={() => setEditingKiosk(null)}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activation Code Display Modal */}
      {createdKiosk && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4 text-green-600">
              {createdKiosk.status === 'pending' && !createdKiosk.activated_at
                ? 'Kiosk Created Successfully!'
                : 'Kiosk Reset Successfully!'}
            </h3>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {createdKiosk.status === 'pending' && !createdKiosk.activated_at
                  ? 'Write down this activation code. It will be needed to activate the kiosk.'
                  : 'A new activation code has been generated. The kiosk will need to be activated again using this code. The UUID remains the same.'}
              </p>
              <div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600 mb-2">New Activation Code:</p>
                <p className="text-3xl font-bold font-mono text-blue-600">{createdKiosk.activation_code}</p>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Kiosk Name:</strong> {createdKiosk.name}</p>
                {createdKiosk.location && <p><strong>Location:</strong> {createdKiosk.location}</p>}
                {createdKiosk.uuid && (
                  <p><strong>UUID:</strong> <span className="font-mono text-xs">{createdKiosk.uuid}</span></p>
                )}
              </div>
            </div>
            <div className="mt-6">
              <button
                onClick={closeActivationModal}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KioskManagement;
